import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';

import {
  NotificationListItem,
  NotificationListStates,
} from '@/components/notifications/notification-list-item';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Button } from '@/components/ui/button';
import { SwitchField } from '@/components/ui/switch';
import {
  useConsoleNotifications,
  useHasConsoleNotificationAccess,
  useMarkConsoleNotificationsRead,
} from '@/hooks/use-console-notifications';

export const NotificationsPage = () => {
  const { t } = useTranslation('notifications');
  const { t: tCommon } = useTranslation();
  const hasAccess = useHasConsoleNotificationAccess();

  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const pageSize = 25;

  const listQuery = useConsoleNotifications({ page, pageSize, unreadOnly });
  const markRead = useMarkConsoleNotificationsRead();

  if (!hasAccess) {
    return <Navigate to="/home" replace />;
  }

  const notifications = listQuery.data?.data ?? [];
  const pageMeta = listQuery.data?.page;
  const unreadCount = listQuery.data?.totals.unreadCount ?? 0;

  const handleOpen = (notification: { id: string; readAt: string | null }) => {
    if (notification.readAt != null) return;
    markRead.mutate({ notificationIds: [notification.id] });
  };

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('unreadCount', { count: unreadCount })}
        headerActions={
          unreadCount > 0 ? (
            <Button
              type="button"
              variant="secondary"
              disabled={markRead.isPending}
              onClick={() => markRead.mutate({ readAll: true })}
            >
              {t('markAllRead')}
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4">
        <SwitchField
          id="notifications-unread-only"
          label={t('unreadOnly')}
          checked={unreadOnly}
          onCheckedChange={(checked) => {
            setUnreadOnly(checked);
            setPage(1);
          }}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-surface-raised">
        <NotificationListStates
          loading={listQuery.isLoading}
          error={listQuery.isError}
          empty={!listQuery.isLoading && !listQuery.isError && notifications.length === 0}
          onRetry={() => {
            listQuery.refetch();
          }}
        />
        {notifications.length > 0 ? (
          <div className="divide-y divide-border p-1">
            {notifications.map((notification) => (
              <NotificationListItem
                key={notification.id}
                notification={notification}
                onOpen={handleOpen}
              />
            ))}
          </div>
        ) : null}
        {pageMeta && pageMeta.totalItems > 0 ? (
          <div className="px-3 pb-3">
            <Pagination
              page={pageMeta.page}
              pageSize={pageMeta.pageSize}
              totalItems={pageMeta.totalItems}
              totalPages={pageMeta.totalPages}
              onPageChange={setPage}
              labels={{
                showing: ({ from, to, total }) => `${from}–${to} / ${total}`,
                perPage: tCommon('pagination.perPage'),
                first: tCommon('pagination.first'),
                previous: tCommon('pagination.previous'),
                next: tCommon('pagination.next'),
                last: tCommon('pagination.last'),
              }}
              disabled={listQuery.isFetching}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
